import { useState, useEffect, useCallback, useMemo } from 'react';
import type { HeroStats, TierHero, AvailableData } from '../types';
import { tierBandCounts } from '../utils/tierBandCounts';

const WORKER_URL = 'https://mlbb-stats-worker.waddle-dee-production.workers.dev';

interface UseTierListReturn {
  heroes: HeroStats[];
  tierHeroes: TierHero[];
  availableData: AvailableData[];
  loading: boolean;
  dataLoading: boolean;
  error: string | null;
  scrapedAt: string | null;
  bigrank: number;
  setBigrank: (bigrank: number) => void;
  refetchData: () => void;
  calculateTiers: (heroes: HeroStats[], beta: number) => TierHero[];
}

export function useTierList(): UseTierListReturn {
  const [heroes, setHeroes] = useState<HeroStats[]>([]);
  const [availableData, setAvailableData] = useState<AvailableData[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scrapedAt, setScrapedAt] = useState<string | null>(null);
  const [bigrank, setBigrank] = useState<number>(0);

  const calculateTierScore = (hero: HeroStats, betaValue: number): number => {
    const winRateScore = hero.win_rate * 100;
    const popularityScore = (hero.pick_rate * 10 + hero.ban_rate) * 100;
    return winRateScore + (betaValue * popularityScore);
  };

  const calculateTiers = useCallback((heroList: HeroStats[], beta: number): TierHero[] => {
    const calculated = heroList.map(hero => ({
      hero,
      tierScore: calculateTierScore(hero, beta)
    }));

    calculated.sort((a, b) => b.tierScore - a.tierScore);

    const total = calculated.length;
    const { s: sCount, a: aCount, b: bCount, c: cCount } = tierBandCounts(total);

    return calculated.map((item, index) => {
      let tier: 'S' | 'A' | 'B' | 'C' | 'D';
      if (index < sCount) tier = 'S';
      else if (index < sCount + aCount) tier = 'A';
      else if (index < sCount + aCount + bCount) tier = 'B';
      else if (index < sCount + aCount + bCount + cCount) tier = 'C';
      else tier = 'D';
      
      return { ...item, tier };
    });
  }, []);

  const fetchAvailableData = useCallback(async () => {
    setDataLoading(true);
    try {
      const response = await fetch(`${WORKER_URL}/api/stats/available`);
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        setAvailableData(data.data);
        const past1DayData = data.data.filter((d: AvailableData) => d.period === 'past_1_days');
        if (past1DayData.length > 0) {
          const highestRank = Math.max(...past1DayData.map((d: AvailableData) => d.bigrank));
          setBigrank(highestRank);
        } else {
          setError('past_1_days のデータがありません。Data Management でデータを取得してください。');
        }
      } else {
        setError('利用可能なデータがありません。Data Management でデータを取得してください。');
      }
    } catch (err) {
      setError('データ一覧の取得に失敗しました');
      console.error('Fetch available data error:', err);
    } finally {
      setDataLoading(false);
    }
  }, []);

  const fetchHeroStats = useCallback(async () => {
    if (!bigrank) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${WORKER_URL}/api/stats/latest?period=past_1_days&bigrank=${bigrank}`
      );

      const data = await response.json();

      if (data.success) {
        const raw = data.data.heroes as HeroStats[];
        setHeroes(raw.map((h) => ({ ...h, lanes: h.lanes ?? [] })));
        setScrapedAt(data.data.scraped_at);
      } else {
        setError(data.message || 'データの取得に失敗しました');
      }
    } catch (err) {
      setError('ネットワークエラー: データを取得できませんでした');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [bigrank]);

  const refetchData = useCallback(() => {
    fetchAvailableData();
  }, [fetchAvailableData]);

  useEffect(() => {
    fetchAvailableData();
  }, [fetchAvailableData]);

  useEffect(() => {
    fetchHeroStats();
  }, [fetchHeroStats]);

  const tierHeroes = useMemo(() => {
    // This will be calculated with beta in the component
    return [];
  }, []);

  return {
    heroes,
    tierHeroes,
    availableData,
    loading,
    dataLoading,
    error,
    scrapedAt,
    bigrank,
    setBigrank,
    refetchData,
    calculateTiers
  };
}
